import { useState, useCallback } from 'react';
import { X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import { useAccountStore } from '../../stores/accountStore';
import { ExchangeOrderBook } from '@/components/organisms/ExchangeOrderBook';
import { useHydraOrderbookWarmup } from '@/hooks/useHydraOrderbookWarmup';
import type { GameRow, TeamSide } from './types';
import { bestPriceCentsForOrderSide, outcomeSideLabel } from './tradeModalBook';

export interface TradeIntent {
  side: 'away' | 'home';
  venue: 'kalshi' | 'polymarket';
}

interface GameDetailPanelProps {
  game: GameRow;
  onClose: () => void;
  tradeIntent?: TradeIntent | null;
}

// ── Order Entry Form ────────────────────────────────────────────

function OrderEntryForm({ game, selectedSide }: { game: GameRow; selectedSide: TeamSide }) {
  const status = useAccountStore((s) => s.status);
  const placeOrder = useAccountStore((s) => s.placeOrder);

  const [orderSide, setOrderSide] = useState<'yes' | 'no'>('yes');
  const [price, setPrice] = useState('');
  const [qty, setQty] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  // Get the Kalshi ticker for the selected team
  const kalshiBook = selectedSide === 'away' ? game.kalshi?.away : game.kalshi?.home;
  const ticker = kalshiBook?.marketTicker ?? '';

  const bestYesHint = bestPriceCentsForOrderSide(kalshiBook, 'yes');
  const bestNoHint = bestPriceCentsForOrderSide(kalshiBook, 'no');

  const applyOrderSide = useCallback(
    (next: 'yes' | 'no') => {
      setOrderSide(next);
      setResult(null);
      const hint = bestPriceCentsForOrderSide(kalshiBook, next);
      if (hint !== undefined) setPrice(String(hint));
    },
    [kalshiBook],
  );

  const handleSubmit = useCallback(async () => {
    const priceCents = parseInt(price, 10);
    const count = parseInt(qty, 10);

    if (!ticker) { setResult({ ok: false, msg: 'No Kalshi market found' }); return; }
    if (isNaN(priceCents) || priceCents < 1 || priceCents > 99) { setResult({ ok: false, msg: 'Price must be 1-99¢' }); return; }
    if (isNaN(count) || count < 1) { setResult({ ok: false, msg: 'Qty must be at least 1' }); return; }

    setSubmitting(true);
    setResult(null);
    try {
      await placeOrder({
        ticker,
        side: orderSide,
        action: 'buy',
        type: 'limit',
        count,
        price: priceCents,
      });
      setResult({ ok: true, msg: `Order placed: ${count} ${orderSide.toUpperCase()} @ ${priceCents}¢` });
      setQty('');
    } catch (err) {
      setResult({ ok: false, msg: err instanceof Error ? err.message : 'Order failed' });
    } finally {
      setSubmitting(false);
    }
  }, [ticker, orderSide, price, qty, placeOrder]);

  if (status !== 'connected') {
    return (
      <div className="border-border border-b px-4 py-3">
        <p className="text-muted-foreground text-xs">Connect your Kalshi account in Settings to trade</p>
      </div>
    );
  }

  if (!ticker) return null;

  const priceCents = parseInt(price, 10) || 0;
  const count = parseInt(qty, 10) || 0;
  const cost = (priceCents * count) / 100;
  const payout = count - cost;

  return (
    <div className="border-border space-y-3 border-b px-4 py-3">
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground text-xs font-medium">Order Entry</span>
        <span className="text-muted-foreground/50 font-mono text-[10px]">{ticker.length > 30 ? `...${ticker.slice(-28)}` : ticker}</span>
      </div>

      {/* Side toggle */}
      <div className="space-y-1.5">
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => applyOrderSide('yes')}
            className={cn(
              'flex-1 rounded-md py-1.5 text-xs font-semibold transition-colors',
              orderSide === 'yes'
                ? 'bg-positive text-white'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            )}
          >
            BUY YES
          </button>
          <button
            type="button"
            onClick={() => applyOrderSide('no')}
            className={cn(
              'flex-1 rounded-md py-1.5 text-xs font-semibold transition-colors',
              orderSide === 'no'
                ? 'bg-negative text-white'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            )}
          >
            BUY NO
          </button>
        </div>
        <p className="text-muted-foreground text-center text-[11px]">
          {outcomeSideLabel(game, selectedSide, orderSide)}
        </p>
      </div>

      {/* Price + Qty */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-muted-foreground mb-1 block text-[10px]">Price (¢)</label>
          <input
            type="number"
            min={1}
            max={99}
            value={price}
            onChange={(e) => { setPrice(e.target.value); setResult(null); }}
            placeholder={
              (orderSide === 'yes' ? bestYesHint : bestNoHint) !== undefined
                ? String(orderSide === 'yes' ? bestYesHint : bestNoHint)
                : '50'
            }
            className="border-border bg-input text-foreground placeholder-muted-foreground focus:border-primary w-full rounded-md border px-2.5 py-1.5 font-mono text-xs focus:outline-none"
          />
        </div>
        <div>
          <label className="text-muted-foreground mb-1 block text-[10px]">Qty</label>
          <input
            type="number"
            min={1}
            value={qty}
            onChange={(e) => { setQty(e.target.value); setResult(null); }}
            placeholder="0"
            className="border-border bg-input text-foreground placeholder-muted-foreground focus:border-primary w-full rounded-md border px-2.5 py-1.5 font-mono text-xs focus:outline-none"
          />
        </div>
      </div>

      {/* Cost / Payout */}
      {count > 0 && priceCents > 0 && (
        <div className="flex justify-between text-xs">
          <span className="text-muted-foreground">Cost: <span className="text-foreground">${cost.toFixed(2)}</span></span>
          <span className="text-muted-foreground">Payout: <span className="text-positive">${payout.toFixed(2)}</span></span>
        </div>
      )}

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={submitting || !price || !qty}
        className="bg-primary text-primary-foreground hover:bg-primary/90 flex w-full items-center justify-center gap-2 rounded-md py-2 text-xs font-semibold transition-colors disabled:opacity-50"
      >
        {submitting && <Loader2 size={12} className="animate-spin" />}
        {submitting ? 'Placing...' : 'Place Order'}
      </button>

      {/* Result */}
      {result && (
        <div className={cn(
          'rounded-md px-2.5 py-1.5 text-xs',
          result.ok ? 'bg-positive/10 text-positive' : 'bg-negative/10 text-negative'
        )}>
          {result.msg}
        </div>
      )}
    </div>
  );
}

// ── Game Detail Panel ───────────────────────────────────────────

export function GameDetailPanel({ game, onClose, tradeIntent }: GameDetailPanelProps) {
  const [selectedSide, setSelectedSide] = useState<TeamSide>(tradeIntent?.side ?? 'away');

  // Ask Hydra to subscribe upstream to this game's Kalshi orderbooks while the
  // panel is open. No-op until Hydra serves /api/* over HTTPS — see
  // docs/hydra-server-asks.md.
  useHydraOrderbookWarmup([game.kalshi?.away.marketTicker, game.kalshi?.home.marketTicker]);

  const teamName = selectedSide === 'away' ? game.awayName : game.homeName;
  const kalshiBook = selectedSide === 'away' ? game.kalshi?.away : game.kalshi?.home;
  const polyBook = selectedSide === 'away' ? game.polymarket?.away : game.polymarket?.home;

  // Invert NO bids → YES asks so the book shows the correct spread.
  // NO bid at 82¢ = willing to sell YES at 18¢ (100 - 82).
  const kalshiAsks = kalshiBook?.no.map(l => ({ priceCents: 100 - l.priceCents, size: l.size })) ?? [];
  const polyAsks = polyBook?.no.map(l => ({ priceCents: 100 - l.priceCents, size: l.size })) ?? [];

  return (
    <div className="bg-card/95 border-border flex h-full w-96 flex-col border-l">
      {/* Header */}
      <div className="border-border flex items-center justify-between border-b px-4 py-3">
        <div>
          <h3 className="text-foreground text-sm font-semibold">
            {game.awayName} @ {game.homeName}
          </h3>
          <p className="text-muted-foreground mt-0.5 text-xs">
            {game.date} &middot; {game.timePt} PT
          </p>
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:bg-accent hover:text-foreground rounded p-1">
          <X size={16} />
        </button>
      </div>

      {/* Team selector */}
      <div className="border-border flex border-b">
        <button
          className={cn(
            'flex-1 py-2 text-center text-sm font-medium transition-colors',
            selectedSide === 'away'
              ? 'bg-primary/20 text-primary border-primary border-b-2'
              : 'text-muted-foreground hover:text-foreground'
          )}
          onClick={() => setSelectedSide('away')}
        >
          {game.awayName}
        </button>
        <button
          className={cn(
            'flex-1 py-2 text-center text-sm font-medium transition-colors',
            selectedSide === 'home'
              ? 'bg-primary/20 text-primary border-primary border-b-2'
              : 'text-muted-foreground hover:text-foreground'
          )}
          onClick={() => setSelectedSide('home')}
        >
          {game.homeName}
        </button>
      </div>

      {/* Order Entry */}
      <OrderEntryForm game={game} selectedSide={selectedSide} />

      {/* Price summary */}
      <div className="border-border border-b px-4 py-2">
        <div className="grid grid-cols-3 gap-2">
          <div />
          <div className="text-muted-foreground text-center text-[10px] font-semibold uppercase">{game.awayCode}</div>
          <div className="text-muted-foreground text-center text-[10px] font-semibold uppercase">{game.homeCode}</div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="text-xs font-semibold text-blue-400">K</div>
          <div className="text-foreground/80 text-center font-mono text-xs">
            {game.kalshi?.away.priceCents ?? '\u2014'}{'\u00A2'}
          </div>
          <div className="text-foreground/80 text-center font-mono text-xs">
            {game.kalshi?.home.priceCents ?? '\u2014'}{'\u00A2'}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="text-xs font-semibold text-purple-400">P</div>
          <div className="text-foreground/80 text-center font-mono text-xs">
            {game.polymarket?.away.priceCents ?? '\u2014'}{'\u00A2'}
          </div>
          <div className="text-foreground/80 text-center font-mono text-xs">
            {game.polymarket?.home.priceCents ?? '\u2014'}{'\u00A2'}
          </div>
        </div>
      </div>

      {/* Orderbooks */}
      <div className="flex-1 space-y-3 overflow-y-auto p-3">
        {kalshiBook && (
          <ExchangeOrderBook
            venue="kalshi"
            teamLabel={teamName}
            bids={kalshiBook.yes}
            asks={kalshiAsks}
          />
        )}
        {polyBook && (
          <ExchangeOrderBook
            venue="polymarket"
            teamLabel={teamName}
            bids={polyBook.yes}
            asks={polyAsks}
          />
        )}
      </div>
    </div>
  );
}
